import React, { useState, useMemo, useEffect } from "react";
import Swal from "sweetalert2";
import { post } from "../../helper/api_helper";

const initialFormState = {
    question_id: 0,
    tobacco_product_type_id_fk:'',
    question_text: "",
    status: "1",
};

const Question = () => {
    const [formData, setFormData] = useState(initialFormState);
    const [options, setOptions] = useState([{ id: Date.now(), option_label: "", score: "" }]);
    const [validated, setValidated] = useState(false);
    const [tobaccoProductTypeList, setTobaccoProductTypeList] = useState([]);
    const [questionList, setQuestionList] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 5;

    // ── Stats ──────────────────────────────────────────────────────────────────
    const allOptions = useMemo(() => questionList.flatMap((q) => q.options || []), [questionList]);

    const stats = useMemo(() => ({
        total: questionList.length,
        active: questionList.filter((q) => String(q.status) === "1").length,
        smoke: questionList.filter((q) => String(q.tobacco_product_type_id_fk) === "1").length,
        smokeless: questionList.filter((q) => String(q.tobacco_product_type_id_fk) === "2").length,
    }), [questionList, allOptions]);

    // ── Filtered / paginated ───────────────────────────────────────────────────
    const filtered = useMemo(
        () => questionList.filter((q) =>
            q.question_text.toLowerCase().includes(search.toLowerCase())
        ),
        [questionList, search]
    );

    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

    // ── Helpers ────────────────────────────────────────────────────────────────
    const showAlert = (icon, title, text, timer = 2000) => {
        Swal.fire({ icon, title, text: text || "Something went wrong.", timer, showConfirmButton: false });
    };

    // ── Option row handlers ────────────────────────────────────────────────────
    const addOptionRow = () =>
        setOptions((prev) => [...prev, { id: Date.now(), question_option_id: 0,option_label: "", score: "" }]);

    const removeOptionRow = (id) =>
        setOptions((prev) => prev.filter((o) => o.id !== id));

    const updateOptionRow = (id, field, value) =>
        setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, [field]: value } : o)));

    // ── Form handlers ──────────────────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleEdit = (row) => {
        setFormData({
            question_id: row.question_id,
            tobacco_product_type_id_fk : row.tobacco_product_type_id_fk,
            question_text: row.question_text,
            status: String(row.status),
        });
        setOptions(
            (row.options || []).map((o) => ({
                id: o.option_id || Date.now() + Math.random(),
                question_option_id : o.question_option_id,
                option_label: o.option_label,
                score: String(o.score),
            }))
        );
    };

    const handleReset = () => {
        setFormData(initialFormState);
        setOptions([{ id: Date.now(), question_option_id : 0,option_label: "", score: "" }]);
        setValidated(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        if (form.checkValidity() === false) {
            e.stopPropagation();
            showAlert("error", "Oops...", "Please fill in all required fields.");
            setValidated(true);
            return;
        }
        try {
            setValidated(false);
            const cleanOptions = options
                .filter((o) => o.option_label.trim())
                .map((o) => ({ question_option_id: o.question_option_id,option_label: o.option_label, score: parseInt(o.score) || 0 }));
            const payload = { ...formData, options: cleanOptions };
            const response = await post("/masters/question", payload);
            if (response.status === 200) {
                showAlert("success", "Saved", response.message);
                fetchData();
                handleReset();
            } else {
                showAlert("error", response.status, response.message);
            }
        } catch (err) {
            showAlert("error", "Unexpected Error", err.message);
        }
    };

    // ── Fetch ──────────────────────────────────────────────────────────────────
    const fetchData = async () => {
        try {
            const response = await post("/masters/question_list");
            if (response.status === 200) {
                setQuestionList(response.data);
            } else {
                showAlert("error", response.status, response.message);
            }
        } catch (err) {
            showAlert("error", "Unexpected Error", err.message);
        }
    };

    const fetchProductTypeData = async () => {
        try {
            const response = await post("/masters/tobacco_product_type_list");
            if (response.status === 200) {
                setTobaccoProductTypeList(response.data);
            } else {
                showAlert('error', response.status, response.message);
            }
        } catch (err) {
            showAlert('error', 'Unexpected Error', err.message);
        }
    };

    // ── Export ─────────────────────────────────────────────────────────────────
    const handleExport = () => {
        const rows = ["ID,Question,Options,Status,Added Date"];
        questionList.forEach((q) =>
            rows.push(
                `${q.question_id},"${q.question_text}",${(q.options || []).length},${String(q.status) === "1" ? "Active" : "Inactive"},"${q.added_date}"`
            )
        );
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([rows.join("\n")], { type: "text/csv" }));
        a.download = "questions.csv";
        a.click();
    };

    useEffect(() => {
        fetchData();
        fetchProductTypeData();
    }, []);

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <>
            {/* ── Stats Row ── */}
            <div className="row mb-4">
                {[
                    { label: "TOTAL QUESTIONS", value: stats.total, color: "#1e293b", sub: "All questions" },
                    { label: "ACTIVE", value: stats.active, color: "#16a34a", sub: "Across all questions" },
                    { label: "SMOKE QUESTIONS", value: stats.smoke, color: "#7c3aed", sub: "Across smoke questions" },
                    { label: "SMOKELESS QUESTIONS", value: stats.smokeless, color: "#d97706", sub: "Across smokeless questions" },
                ].map((s) => (
                    <div className="col-lg-3 col-md-6" key={s.label}>
                        <div className="card mb-3">
                            <div className="card-body">
                                <p className="mb-1 text-uppercase fw-bold" style={{ fontSize: "11px", letterSpacing: "0.8px", color: "#94a3b8" }}>
                                    {s.label}
                                </p>
                                <h2 className="mb-1 fw-bold" style={{ fontSize: "34px", color: s.color, lineHeight: 1 }}>
                                    {s.value}
                                </h2>
                                <p className="mb-0" style={{ fontSize: "12px", color: "#94a3b8" }}>{s.sub}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Main Row ── */}
            <div className="row">

                {/* ── Form Panel ── */}
                <div className="col-lg-3">
                    <div className="card">
                        <form noValidate className={validated ? "was-validated" : ""} onSubmit={handleSubmit}>

                            <div className="card-header d-flex align-items-center gap-2">
                                <span style={{ width: "9px", height: "9px", borderRadius: "50%", background: "#3b82f6", display: "inline-block", flexShrink: 0 }} />
                                <h5 className="card-title mb-0">
                                    {formData.question_id ? "Edit Question" : "Add Question"}
                                </h5>
                            </div>

                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-12">
                                        <label>Tobacco Product Type</label>
                                        <select
                                            name="tobacco_product_type_id_fk"
                                            className="form-control radius-4 form-select"
                                            value={formData.tobacco_product_type_id_fk}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select</option>
                                            {tobaccoProductTypeList.map((tobacco_product_type) => (
                                                <option key={tobacco_product_type.tobacco_product_type_id} value={tobacco_product_type.tobacco_product_type_id}>
                                                {tobacco_product_type.tobacco_product_type_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {/* Question Text */}
                                    <div className="col-md-12">
                                        <label className="form-label text-uppercase fw-bold" style={{ fontSize: "11px", letterSpacing: "0.7px", color: "#64748b" }}>
                                            Question Text
                                        </label>
                                        <textarea
                                            className="form-control"
                                            name="question_text"
                                            value={formData.question_text}
                                            onChange={handleChange}
                                            placeholder="Enter question text..."
                                            rows={3}
                                            required
                                        />
                                    </div>

                                    {/* Status */}
                                    <div className="col-md-12">
                                        <label className="form-label text-uppercase fw-bold" style={{ fontSize: "11px", letterSpacing: "0.7px", color: "#64748b" }}>
                                            Status
                                        </label>
                                        <select
                                            className="form-control form-select"
                                            name="status"
                                            value={formData.status}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="" disabled>Select</option>
                                            <option value="1">Active</option>
                                            <option value="0">In-Active</option>
                                        </select>
                                    </div>

                                    {/* Answer Options */}
                                    <div className="col-md-12">
                                        <hr className="my-2" />
                                        <label className="form-label text-uppercase fw-bold" style={{ fontSize: "11px", letterSpacing: "0.7px", color: "#64748b" }}>
                                            Answer Options
                                        </label>
                                        <div className="d-flex justify-content-between mb-1" style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600 }}>
                                            <span>Option Label</span>
                                            <span style={{ marginRight: "38px" }}>Score</span>
                                        </div>

                                        {options.map((opt) => (
                                            <div key={opt.id} className="d-flex gap-2 align-items-center mb-2">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Option label..."
                                                    value={opt.option_label}
                                                    onChange={(e) => updateOptionRow(opt.id, "option_label", e.target.value)}
                                                />
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    placeholder="Score"
                                                    min="0"
                                                    value={opt.score}
                                                    onChange={(e) => updateOptionRow(opt.id, "score", e.target.value)}
                                                    style={{ width: "72px", flexShrink: 0 }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm"
                                                    style={{ border: "1px solid #fecaca", background: "#fff5f5", color: "#ef4444", flexShrink: 0, width: "30px", height: "30px", padding: "10px" }}
                                                    onClick={() => removeOptionRow(opt.id)}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}

                                        <button
                                            type="button"
                                            className="btn btn-sm w-100 mt-1"
                                            style={{ border: "1.5px dashed #93c5fd", background: "transparent", color: "#3b82f6", fontWeight: 600 }}
                                            onClick={addOptionRow}
                                        >
                                            + Add Option
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="card-footer">
                                <div className="d-flex gap-2">
                                    <button type="submit" className="btn btn-primary btn-sm flex-fill">
                                        💾 Save Question
                                    </button>
                                    <button type="button" className="btn btn-secondary btn-sm" onClick={handleReset}>
                                        ✕
                                    </button>
                                </div>
                            </div>

                        </form>
                    </div>
                </div>

                {/* ── Table Panel ── */}
                <div className="col-lg-9">
                    <div className="card basic-data-table">

                        {/* Toolbar */}
                        <div className="card-header d-flex align-items-center justify-content-between">
                            <button
                                type="button"
                                className="btn btn-sm"
                                style={{ background: "#16a34a", color: "#fff", fontWeight: 700, border: "none" }}
                                onClick={handleExport}
                            >
                                ⬇ Export
                            </button>
                            <div className="position-relative">
                                <span className="position-absolute" style={{ left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "13px", pointerEvents: "none" }}>
                                    🔍
                                </span>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search questions..."
                                    value={search}
                                    onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                                    style={{ paddingLeft: "30px", width: "220px" }}
                                />
                            </div>
                        </div>

                        {/* Table */}
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: "#94a3b8" }}>Actions</th>
                                            <th style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: "#94a3b8" }}>Type</th>
                                            <th style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: "#94a3b8" }}>Question Text ↕</th>
                                            <th style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: "#94a3b8" }}>Options</th>
                                            <th style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: "#94a3b8" }}>Status</th>
                                            <th style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: "#94a3b8" }}>Added Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginated.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="text-center py-5 text-muted">
                                                    No questions found
                                                </td>
                                            </tr>
                                        ) : (
                                            paginated.map((q) => (
                                                <tr key={q.question_id}>
                                                    <td style={{ verticalAlign: "middle", width: "80px",display: "flex" }}>
                                                        <i
                                                            className="ri-file-edit-fill text-primary cursor-pointer d-block mb-1"
                                                            style={{ fontSize: "18px",padding: "10px" }}
                                                            onClick={() => handleEdit(q)}
                                                            title="Edit"
                                                        />
                                                    </td>
                                                    <td style={{ verticalAlign: "middle" }}>
                                                        <div className="fw-semibold mb-1" style={{ color: "#1e293b", lineHeight: 1.4 }}>
                                                            {q.tobacco_product_type_name}
                                                        </div>
                                                    </td>
                                                    <td style={{ verticalAlign: "middle" }}>
                                                        <div className="fw-semibold mb-1" style={{ color: "#1e293b", lineHeight: 1.4 }}>
                                                            {q.question_text}
                                                        </div>
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {(q.options || []).map((o, i) => (
                                                                <span
                                                                    key={i}
                                                                    className="badge"
                                                                    style={{ background: "#f1f5f9", color: "#475569", fontWeight: 500, fontSize: "11px" }}
                                                                >
                                                                    {o.option_label}{" "}
                                                                    <span style={{ color: "#8b5cf6", fontWeight: 700 }}>({o.score})</span>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td style={{ verticalAlign: "middle", width: "100px" }}>
                                                        <span className="badge" style={{ background: "#ede9fe", color: "#6d28d9", fontWeight: 700, fontSize: "12px", padding: "5px 10px", borderRadius: "20px" }}>
                                                            {(q.options || []).length} options
                                                        </span>
                                                    </td>
                                                    <td style={{ verticalAlign: "middle", width: "90px" }}>
                                                        {String(q.status) === "1" ? (
                                                            <span className="badge" style={{ background: "#dcfce7", color: "#15803d", fontWeight: 700, fontSize: "11px" }}>Active</span>
                                                        ) : (
                                                            <span className="badge" style={{ background: "#f1f5f9", color: "#64748b", fontWeight: 700, fontSize: "11px" }}>Inactive</span>
                                                        )}
                                                    </td>
                                                    <td style={{ verticalAlign: "middle", width: "130px", fontSize: "12px", color: "#94a3b8" }}>
                                                        {q.date_added}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Footer / Pagination */}
                        <div className="card-footer d-flex align-items-center justify-content-between" style={{ fontSize: "13px", color: "#64748b" }}>
                            <span>
                                Showing{" "}
                                <strong>
                                    {filtered.length === 0 ? 0 : (currentPage - 1) * perPage + 1}–{Math.min(currentPage * perPage, filtered.length)}
                                </strong>
                                {" "}of <strong>{filtered.length}</strong> results
                            </span>
                            <div className="d-flex gap-1">
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage((p) => p - 1)}
                                    style={{ width: "32px", padding: "4px" }}
                                >
                                    ‹
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                                    <button
                                        key={n}
                                        className={`btn btn-sm ${n === currentPage ? "btn-primary" : "btn-outline-secondary"}`}
                                        onClick={() => setCurrentPage(n)}
                                        style={{ width: "32px", padding: "4px" }}
                                    >
                                        {n}
                                    </button>
                                ))}
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage((p) => p + 1)}
                                    style={{ width: "32px", padding: "4px" }}
                                >
                                    ›
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

export default Question;